// Misc
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import some from 'lodash/some'
import reject from 'lodash/reject'
import includes from 'lodash/includes'
import { withStyles } from '@material-ui/core/styles'

// Utils
import { createCustomField } from 'utils/proposals'

// Constants
import {
  LONG_TEXT, SHORT_TEXT, DESCRIPTION, PRICE,
  DURATION, LINK, YES_NO, CHOICE, WHEN_SERVICE, WHEN_EVENT,
  MESSAGE, SEATS,
  NEWSLETTER_SUBSCRIBE,
  LOCATION, LOCATION_CHOICE, LOCATION_SUGGEST,
  LIVE_CHAT, LIVE_CHAT_WELCOME_MESSAGE, LIVE_CHAT_AVAILABILITY, LIVE_CHAT_REQUIRE_USER_INFO
} from 'constants/proposalFieldTypes'
import { SCHEDULED_SERVICE, SINGLE_EVENT } from 'constants/proposalTypes'
import {
  messageFieldTemplate,
  locationFieldTemplate, locationChoiceFieldTemplate, locationSuggestFieldTemplate,
  liveChatWelcomeMessageFieldTemplate,
} from 'constants/proposalCreationTemplates'

// Atoms
import ItemsGrid from 'atoms/ItemsGrid'
import Button from 'atoms/Button'
import Spacer from 'atoms/Spacer'
import Expand from 'atoms/Expand'

// Styles
const styles = theme => ({
  buttonsGroup: {
    '&:not(:last-child)': {
      paddingBottom: theme.spacing.unit,
    }
  }
})

export default
@withStyles(styles)
class ProposalFieldCreationButtons extends Component {
  static propTypes = {
    proposal: PropTypes.object.isRequired,
    onProposalUpdate: PropTypes.func.isRequired,
    normalizedFields: PropTypes.array.isRequired,
    classes: PropTypes.object.isRequired,
  }

  state = {
    expanded: false
  }

  getDescriptionProposalData() {
    if (this.isFieldExists(DESCRIPTION)) return null
    return { data: { fields: this.addField(DESCRIPTION) } }
  }

  getPriceProposalData() {
    if (this.isFieldExists(PRICE) || this.isFieldForbidden(PRICE)) return null
    return { data: { fields: this.addField(PRICE) } }
  }

  getDurationProposalData() {
    if (this.isFieldExists(DURATION) || this.isFieldForbidden(DURATION)) return null
    return { data: { fields: this.addField(DURATION) } }
  }

  getLinkProposalData() {
    if (this.isFieldExists(LINK)) return null
    return { data: { fields: this.addField(LINK) } }
  }

  getMessageProposalData() {
    if (this.isFieldExists(MESSAGE)) return null
    return { data: { fields: this.addField(MESSAGE, messageFieldTemplate) } }
  }

  getLiveChatWelcomeMessageProposalData() {
    if (this.isFieldExists(LIVE_CHAT_WELCOME_MESSAGE)) return null
    return { data: { fields: this.addField(
      LIVE_CHAT_WELCOME_MESSAGE,
      liveChatWelcomeMessageFieldTemplate
    ) } }
  }

  getLiveChatAvailabilityProposalData() {
    if (this.isFieldExists(LIVE_CHAT_AVAILABILITY)) return null
    return { data: { fields: this.addField(LIVE_CHAT_AVAILABILITY) } }
  }

  getLiveChatRequireUserInfo() {
    if (this.isFieldExists(LIVE_CHAT_REQUIRE_USER_INFO)) return null
    return { data: { fields: this.addField(LIVE_CHAT_REQUIRE_USER_INFO) } }
  }

  getLongTextProposalData() {
    return { data: { fields: this.addField(LONG_TEXT) } }
  }

  getShortTextProposalData() {
    return { data: { fields: this.addField(SHORT_TEXT) } }
  }

  getYesNoProposalData() {
    return { data: { fields: this.addField(YES_NO) } }
  }

  getChoiceProposalData() {
    return { data: { fields: this.addField(CHOICE) } }
  }

  getWhenServiceProposalData() {
    if (
      this.isFieldExists(WHEN_SERVICE)
      || this.isFieldExists(WHEN_EVENT)
      || this.isFieldForbidden(WHEN_SERVICE)
    ) return null
    return { type: SCHEDULED_SERVICE }
  }

  getWhenEventProposalData() {
    if (
      this.isFieldExists(WHEN_EVENT)
      || this.isFieldExists(WHEN_SERVICE)
      || this.isFieldForbidden(WHEN_EVENT)
    ) return null
    return { type: SINGLE_EVENT, selectOnlyDate: true }
  }

  getSeatsProposalData() {
    if (
      this.isFieldExists(SEATS)
      || this.isFieldForbidden(SEATS)
      || !this.isFieldExists(WHEN_EVENT)
    ) return null
    return { data: { fields: this.addField(SEATS) } }
  }

  getLocationProposalData() {
    if (this.isFieldExists(LOCATION)) return null
    return { data: { fields: this.addField(
      LOCATION,
      locationFieldTemplate,
      [LOCATION_CHOICE, LOCATION_SUGGEST]
    ) } }
  }

  getLocationChoiceProposalData() {
    if (this.isFieldExists(LOCATION_CHOICE)) return null
    return { data: { fields: this.addField(
      LOCATION_CHOICE,
      locationChoiceFieldTemplate,
      [LOCATION, LOCATION_SUGGEST]
    ) } }
  }

  getLocationSuggestProposalData() {
    if (this.isFieldExists(LOCATION_SUGGEST)) return null
    return { data: { fields: this.addField(
      LOCATION_SUGGEST,
      locationSuggestFieldTemplate,
      [LOCATION, LOCATION_CHOICE]
    ) } }
  }

  getNewsletterSubscribeProposalData() {
    if (this.isFieldExists(NEWSLETTER_SUBSCRIBE)) return null
    return { data: { fields: this.addField(NEWSLETTER_SUBSCRIBE) } }
  }

  isFieldExists = (fieldType) => {
    const { normalizedFields } = this.props
    return some(normalizedFields, { type: fieldType })
  }

  isFieldForbidden = (fieldType) => {
    const { proposal } = this.props
    return includes(proposal.data.forbiddenFields, fieldType)
  }

  addField = (fieldType, data, fieldsToRemove) => {
    const { normalizedFields } = this.props
    const fields = fieldsToRemove
      ? reject(normalizedFields, ({ type }) => includes(fieldsToRemove, type))
      : normalizedFields
    return [...fields, createCustomField(fieldType, data)]
  }

  handleExpandToggle = () => {
    const { expanded } = this.state
    this.setState({ expanded: !expanded })
  }

  handleButtonClick = proposalData => () => {
    const { onProposalUpdate } = this.props
    this.setState({ expanded: false }, () => {
      onProposalUpdate(proposalData)
    })
  }

  renderButtonsGroup(color, fields) {
    const { classes } = this.props
    const hasSome = some(fields, field => !!field.proposalData)
    return hasSome && (
      <div className={classes.buttonsGroup}>
        <ItemsGrid>
          { fields.map(({ label, proposalData }) => (proposalData && (
            <Button
              onClick={this.handleButtonClick(proposalData)}
              color={color}
              key={label}
            >
              { label }
            </Button>
          ))) }
        </ItemsGrid>
      </div>
    )
  }

  renderButtons() {
    const purpleFields = [
      { label: 'Message', proposalData: this.getMessageProposalData() },
      { label: 'Long text', proposalData: this.getLongTextProposalData() },
      { label: 'Short text', proposalData: this.getShortTextProposalData() },
      { label: 'Yes/ No', proposalData: this.getYesNoProposalData() },
      { label: 'Multiple choice', proposalData: this.getChoiceProposalData() },
    ]

    const violetFields = [
      { label: 'Description', proposalData: this.getDescriptionProposalData() },
      { label: 'Price', proposalData: this.getPriceProposalData() },
      { label: 'Duration', proposalData: this.getDurationProposalData() },
      { label: 'Link', proposalData: this.getLinkProposalData() },
    ]

    const cyanFields = [
      { label: 'Client suggests date & time', proposalData: this.getWhenServiceProposalData() },
      { label: 'Specify a date & time', proposalData: this.getWhenEventProposalData() },
    ]

    const blueFields = [
      { label: 'Set capacity', proposalData: this.getSeatsProposalData() },
      { label: 'Specify location', proposalData: this.getLocationProposalData() },
      { label: 'Add multiple locations', proposalData: this.getLocationChoiceProposalData() },
      { label: 'Client suggests location', proposalData: this.getLocationSuggestProposalData() },
      { label: 'Subscribe to newsletter', proposalData: this.getNewsletterSubscribeProposalData() },
    ]

    return (
      <Fragment>
        { this.renderButtonsGroup('purple', purpleFields) }
        { this.renderButtonsGroup('violet', violetFields) }
        { this.renderButtonsGroup('cyan', cyanFields) }
        { this.renderButtonsGroup('blue', blueFields) }
      </Fragment>
    )
  }

  renderDefault() {
    const { expanded } = this.state
    return (
      <Fragment>
        <Spacer />
        <Expand
          expanded={expanded}
          onToggle={this.handleExpandToggle}
          label="More options"
        >
          { this.renderButtons() }
        </Expand>
      </Fragment>
    )
  }

  renderLiveChatOptions() {
    const blueFields = [
      {
        label: 'Add an auto intro message',
        proposalData: this.getLiveChatWelcomeMessageProposalData()
      },
      {
        label: 'Set live chat availability',
        proposalData: this.getLiveChatAvailabilityProposalData()
      },
      {
        label: 'Require user info',
        proposalData: this.getLiveChatRequireUserInfo()
      },
    ]
    const hasSome = some(blueFields, ({ proposalData }) => !!proposalData)
    return hasSome && (
      <Fragment>
        <Spacer />
        { this.renderButtonsGroup('blue', blueFields) }
      </Fragment>
    )
  }

  render() {
    const { normalizedFields } = this.props
    const isLiveChat = some(normalizedFields, { type: LIVE_CHAT })
    return isLiveChat ? this.renderLiveChatOptions() : this.renderDefault()
  }
}
